// The Shop cart: products a visitor has picked, kept in their own
// browser until they pay. Card products only — a product with its own
// checkout link is sold on that page, not here.
//
// A line is a product NAME, the size picked on our page and how many.
// The price beside it is for showing; the server reads the price off
// the saved block when the checkout opens (WebsiteCheckout::cart), and
// refuses the whole order, naming the product, if a line can no longer
// be sold. So nothing stored here is trusted for money.
//
// Pure functions first, so the harness can run them in node; the Vue
// store under them is a thin wrapper.

export const CART_LINES = 20;
export const CART_QTY = 20;
// WebsiteCheckout::MAX_SHIPPING_FEE.
export const MAX_SHIPPING_FEE = 1000;

/** A Shop block's flat shipping fee as typed, or '' for none. Same
 *  rule as WebsiteCheckout::shippingFee; the server charges its own
 *  reading, this one is for showing. */
export function shippingFee(raw) {
    const fee = String(raw ?? '').trim();
    if (!/^\d+(\.\d{1,2})?$/.test(fee) || Number(fee) <= 0 || Number(fee) > MAX_SHIPPING_FEE) return '';
    return fee;
}

const key = (name, option) => `${name}\u0000${option || ''}`;

function clampQty(n) {
    const q = Math.floor(Number(n) || 0);
    return Math.max(0, Math.min(CART_QTY, q));
}

/** Lines read back from storage, with anything malformed dropped. */
export function normaliseCart(raw) {
    const out = [];
    const seen = new Map();
    for (const r of Array.isArray(raw) ? raw : []) {
        const name = String(r?.name || '').trim().slice(0, 200);
        const qty = clampQty(r?.qty);
        if (!name || qty < 1) continue;
        const option = String(r?.option || '').trim().slice(0, 120);
        const k = key(name, option);
        if (seen.has(k)) {
            const line = seen.get(k);
            line.qty = clampQty(line.qty + qty);
            continue;
        }
        if (out.length >= CART_LINES) break;
        const line = { name, option, qty, price: String(r?.price ?? ''), image: String(r?.image || ''), shipping: shippingFee(r?.shipping) };
        seen.set(k, line);
        out.push(line);
    }
    return out;
}

/** The cart with one more of a product: a new line, or more of the
 *  same product in the same size. Returns the cart unchanged when it
 *  is full, so the caller can say so. */
export function addLine(lines, { name, option = '', price = '', image = '', shipping = '' }, qty = 1) {
    const k = key(name, option);
    const hit = lines.find((l) => key(l.name, l.option) === k);
    if (hit) {
        return lines.map((l) => (l === hit ? { ...l, qty: clampQty(l.qty + qty), price, image, shipping: shippingFee(shipping) } : l));
    }
    if (lines.length >= CART_LINES) return lines;
    return normaliseCart([...lines, { name, option, qty, price, image, shipping }]);
}

export function setQty(lines, index, qty) {
    const q = clampQty(qty);
    return q < 1 ? lines.filter((_, i) => i !== index) : lines.map((l, i) => (i === index ? { ...l, qty: q } : l));
}

export function cartCount(lines) {
    return lines.reduce((n, l) => n + l.qty, 0);
}

/** A display total, or null when a line's price is not a number. */
export function cartTotal(lines) {
    let sum = 0;
    for (const l of lines) {
        if (!/^\d+(\.\d{1,2})?$/.test(String(l.price).trim())) return null;
        sum += Number(l.price) * l.qty;
    }
    return Math.round(sum * 100) / 100;
}

/** The order's shipping: one fee per order, the highest any line's
 *  block charges, as WebsiteCheckout::cart works it out. 0 for none. */
export function cartShipping(lines) {
    return lines.reduce((max, l) => Math.max(max, Number(shippingFee(l.shipping)) || 0), 0);
}

/** The query the checkout reads: parallel item[], option[], qty[]. */
export function cartQuery(lines, returnPath = '') {
    const qs = new URLSearchParams();
    for (const l of lines) {
        qs.append('item[]', l.name);
        qs.append('option[]', l.option || '');
        qs.append('qty[]', String(l.qty));
    }
    if (returnPath) qs.set('return', returnPath);
    return qs.toString();
}
